import { useState, type FormEvent, type ReactNode } from 'react';
import type { BusinessOffering } from '../../types/onboarding';
import type { OtherSaleProduct } from '../../types/otherSales';
import { PRODUCT_NAME_MAX, sanitizePrice, type CreateFailure } from '../../utils/newProduct';
import { buildQuickProductPayload, offeringExample, offeringNoun, validateQuickProduct, type QuickProductErrors } from '../../utils/quickProduct';
import { useProductCreate } from '../product/useProductCreate';
import { Button, Field, Input } from '../ui/primitives';

/**
 * ONB-001 (Macrofase A) — alta rápida de un artículo: nombre y precio, nada más. Crea el artículo REAL
 * del catálogo, el mismo que vende el punto de venta (no hay un catálogo de onboarding).
 *
 * ONB-004: es la variante mínima de «Nuevo producto» (Bloque E), sin motor propio. El borrador, las reglas
 * y el cuerpo salen de `utils/newProduct.ts` (ver `utils/quickProduct.ts`) y el envío es el mismo del
 * asistente (`useProductCreate`): una sola alta aunque se toque dos veces, y sin red no se dice que falló.
 * La usan el recorrido de inicio y la tarjeta del Inicio, que son del dueño.
 */
export function QuickProductForm({ offering, onCreated, onFailed, secondaryAction, submitLabel }: {
  offering: BusinessOffering | null;
  onCreated: (product: OtherSaleProduct) => void;
  /** P-085: aviso opcional de un alta que falló (para el embudo; la pantalla ya muestra su mensaje). */
  onFailed?: (failure: CreateFailure) => void;
  /** Un segundo botón al pie («Lo haré después», «Cancelar»). */
  secondaryAction?: ReactNode;
  submitLabel?: string;
}) {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [errors, setErrors] = useState<QuickProductErrors>({});
  const [serverError, setServerError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const { create, savingRef, createdRef } = useProductCreate();
  const noun = offeringNoun(offering);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (savingRef.current || createdRef.current) return;
    const found = validateQuickProduct({ name, price });
    setErrors(found);
    setServerError(undefined);
    if (found.name || found.price) return;
    await create(buildQuickProductPayload({ name, price }), `No pudimos guardar el ${noun}. Inténtalo de nuevo.`, {
      start: () => setBusy(true),
      created: (product) => onCreated(product),
      failed: (failure) => {
        // Solo pregunta nombre y precio: un nombre repetido va junto al nombre; lo demás, arriba.
        if (failure.screen === 'name') setErrors({ name: failure.message });
        else setServerError(failure.message);
        onFailed?.(failure);
      },
      settled: () => setBusy(false),
    });
  };

  return (
    <form className="n3-quick-product" noValidate onSubmit={(event) => void submit(event)}>
      {serverError && <p className="n3-quick-product__error" role="alert">{serverError}</p>}
      <Field error={errors.name} label="Nombre" required>
        <Input autoComplete="off" maxLength={PRODUCT_NAME_MAX} onChange={(event) => setName(event.target.value)} placeholder={offeringExample(offering)} value={name} />
      </Field>
      <Field error={errors.price} hint="Es lo que se cobra en el punto de venta." label="Precio" required>
        <Input inputMode="decimal" onChange={(event) => setPrice(sanitizePrice(event.target.value))} placeholder="0.00" prefix="$" value={price} />
      </Field>
      <p className="n3-quick-product__note">
        Se guarda «Sin impuestos» y disponible en todas tus tiendas. Puedes cambiarlo después en el catálogo.
      </p>
      <div className="n3-quick-product__actions">
        <Button fullWidth loading={busy} loadingLabel="Guardando…" size="lg" type="submit" variant="primary">{submitLabel ?? `Guardar ${noun}`}</Button>
        {secondaryAction}
      </div>
    </form>
  );
}
